import { useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Card, CardTabs, GetColorStatus, useUpdateQueryParams, withStyles } from 'vopay';
import { Title, UtilButton, TransactionStatus } from 'UI';
import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFetchPartnerPartners from '@/hooks/useApi/useFetchPartnerPartners';
import Details from './tabs/Details/Details';
import Accounts from './tabs/Accounts/Accounts';
import ActiveIcon from './svg/Active.svg';
import PendingIcon from './svg/Pending.svg';
import styles from './PartnerDetails.module.scss';

const cn = withStyles(styles);

type TabKey = 'details' | 'accounts';

export default function PartnerDetailsPage() {
  const { partnerAccountId } = useParams<{ partnerAccountId: string }>();
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.Details' });
  const { t: tStatus } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });
  const { partners, fetchPartnerPartners } = useFetchPartnerPartners();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const location = useLocation();
  const updateQueryParams = useUpdateQueryParams<{ tab: TabKey }>();

  const activeTab = useMemo<TabKey>(
    () => (new URLSearchParams(location.search).get('tab') as TabKey) || 'details',
    [location.search],
  );

  useEffect(() => {
    const loadPartner = async () => {
      try {
        setDataFetchingInfo(true, '', 'fullScreen');
        // ShowToken is requested here because the Accounts tab (built later) needs it.
        await fetchPartnerPartners({ ShowToken: true, SearchText: partnerAccountId });
      } catch (error) {
        alertDialog.error(
          t('errorTitle', { defaultValue: 'Unable to Load Partner' }),
          error instanceof Error ? error.message : '',
        );
      } finally {
        setDataFetchingInfo(false);
      }
    };

    void loadPartner();
  }, [partnerAccountId]);

  const partner = useMemo(
    () => partners.find((partnerAccount) => partnerAccount.PartnerAccountID === partnerAccountId),
    [partners, partnerAccountId],
  );

  return (
    <Box gapSize="24px" flx="col" maxwidth="1120px" className={cn('pageContainer')}>
      <UtilButton size="medium" iconLeft="chevron_left" onClick={() => window.history.back()}>
        {t('backButton', { defaultValue: 'Back' })}
      </UtilButton>

      <Card style={{ padding: '0px' }}>
        <div className={cn('header')}>
          <div className={cn('headerRight')}>
            <img src={partner?.IsActive ? ActiveIcon : PendingIcon} alt="" className={cn('accountIcon')} />

            <div className={cn('titleGroup')}>
              <Title level="3">{partner?.Name}</Title>
            </div>
          </div>

          <div className={cn('statusContainer')}>
            {partner &&
              (partner.IsActive ? (
                <TransactionStatus label={tStatus('Active')} status="positive" />
              ) : (
                partner.AccountStatus && (
                  <TransactionStatus
                    label={tStatus(partner.AccountStatus)}
                    status={GetColorStatus(partner.AccountStatus)}
                  />
                )
              ))}
          </div>
        </div>
      </Card>

      <CardTabs.Root
        value={activeTab}
        onValueChange={(value) => updateQueryParams({ tab: value as TabKey })}
        className={cn('tabsRoot')}
      >
        <CardTabs.List>
          <CardTabs.Trigger value="details">{t('detailsTab', { defaultValue: 'Details' })}</CardTabs.Trigger>
          <CardTabs.Trigger value="accounts">{t('accountsTab', { defaultValue: 'Accounts' })}</CardTabs.Trigger>
        </CardTabs.List>

        <CardTabs.Content value="details">{partner && <Details partner={partner} />}</CardTabs.Content>

        <CardTabs.Content value="accounts">{partner && <Accounts token={partner.Token ?? ''} />}</CardTabs.Content>
      </CardTabs.Root>
    </Box>
  );
}
